import { MACHINE_TYPE_SLUG_PATTERN } from "./machine-type-catalog.js";
import { z } from "zod";
//#region src/machine-type-slug-catalog.ts
/**
* @deprecated compute-classes D5 — superseded by compute CLASSES + the D1 resolver
* (`compute-class.ts`). Never built; zero production consumers. Retained for out-of-tree
* consumers only; deleted in a deliberate major.
*
* The clouds a slug can be mapped onto. Mirrors the platform's provider enum; `aws`/`azure` are
* accepted and OFFLINE — no source implementation and no credentials exist.
*/
const MACHINE_TYPE_CLOUDS = [
	"gcp",
	"aws",
	"azure"
];
/**
* @deprecated compute-classes D5 — superseded by compute CLASSES + the D1 resolver
* (`compute-class.ts`). Never built; zero production consumers. Retained for out-of-tree
* consumers only; deleted in a deliberate major.
*
* The clouds a mapping is REQUIRED for (M-4). A cloud becomes required the day it comes online, at
* which point every existing entry correctly reports as incomplete rather than being grandfathered.
*/
const ONLINE_MACHINE_TYPE_CLOUDS = ["gcp"];
/**
* @deprecated compute-classes D5 — superseded by compute CLASSES + the D1 resolver
* (`compute-class.ts`). Never built; zero production consumers. Retained for out-of-tree
* consumers only; deleted in a deliberate major.
*
* One cloud's concrete machine type for a slug. `.strict()`.
*/
const machineTypeCloudMappingSchema = z.object({
	cloud: z.enum(MACHINE_TYPE_CLOUDS),
	/** The provider's own machine-type name, e.g. `n2-standard-4`. NOT validated against a SKU list —
	*  D3 forbids a re-hardcoded SKU list; the provisioning-svc discovery catalog is authoritative. */
	machineType: z.string().min(1).max(64),
	/** Derived from the cloud catalog, never operator-authored (M-5). */
	cpuMilliCeiling: z.number().int().positive(),
	memoryMiBCeiling: z.number().int().positive()
}).strict();
/**
* @deprecated compute-classes D5 — superseded by compute CLASSES + the D1 resolver
* (`compute-class.ts`). Never built; zero production consumers. Retained for out-of-tree
* consumers only; deleted in a deliberate major.
*
* One operator-curated, SITE-WIDE machine-type slug (R-9 M-1/M-4/M-5/M-6/M-10/M-13).
*
* ⛔ CP-INTERNAL. This is NOT `machineTypeCatalogEntrySchema` and MUST NOT become app-facing. What an
* app sees is slug + display name + vCPU/memory and NOTHING ELSE (M-7): no per-cloud mappings, no
* regional availability. An app author has no use for the string `n2-standard-4`, and the moment they
* can see it they will depend on it — which is the coupling this program removes. It also avoids
* publishing our cloud and region footprint to every app developer.
*/
const machineTypeSlugCatalogEntrySchema = z.object({
	/** ⛔ IMMUTABLE once created (M-10). Fixtures point at the slug, so renaming it would mean
	*  rewriting every referencing manifest. The DISPLAY NAME is what an operator renames. */
	slug: z.string().regex(MACHINE_TYPE_SLUG_PATTERN),
	/** Freely editable (M-10) — the rename the owner intends costs nothing and moves no references. */
	name: z.string().min(1).max(120),
	description: z.string().max(500).optional(),
	/** Per-cloud mappings. A mapping is REQUIRED for every cloud in
	*  {@link ONLINE_MACHINE_TYPE_CLOUDS}; offline clouds are greyed in the UI, not required (M-4).
	*  ⛔ A data plane whose cloud has no mapping REFUSES PLACEMENT with a named reason; it never
	*  substitutes a near-equivalent. */
	mappings: z.array(machineTypeCloudMappingSchema),
	/**
	* ⭐ DERIVED, never authored: the MINIMUM `cpuMilliCeiling` / `memoryMiBCeiling` across the mapped
	* clouds (M-5). The server computes these; a client that sends them is rejected by `.strict()` on
	* the write DTO.
	*
	* ⛔ CONSEQUENCE THAT MUST BE BUILT, NOT DISCOVERED: adding or editing a mapping can SHRINK a slug
	* RETROACTIVELY. The day an AWS mapping lands with a smaller equivalent, this ceiling drops and any
	* fixture already sized above it becomes invalid — a fixture nobody touched, broken by an operator
	* editing a mapping. The mapping editor reports which fixtures now exceed the new minimum BEFORE it
	* commits.
	*/
	maxVCPUMilli: z.number().int().positive(),
	maxMemMiB: z.number().int().positive(),
	/** Exactly one entry in the catalog carries `true` — the slug a data plane's base pool defaults to. */
	isDefault: z.boolean(),
	/** Operator curation. ⛔ Disabling is NOT deletion, and neither is permitted while usage > 0 (M-6,
	*  refined by M-13: polychromatic rollouts mean a slug cannot be removed while ANY ACTIVE VERSION
	*  references it). */
	enabled: z.boolean(),
	/**
	* How many PUBLISHED APP VERSIONS reference this slug (M-6). ⛔ NOT "what the current manifest
	* says": if usage counted only the latest manifest, deleting a slug an older published version
	* still names would break a ROLLBACK. Server-computed; read-only.
	*/
	usageCount: z.number().int().nonnegative()
}).strict();
/**
* @deprecated compute-classes D5 — superseded by compute CLASSES + the D1 resolver
* (`compute-class.ts`). Never built; zero production consumers. Retained for out-of-tree
* consumers only; deleted in a deliberate major.
*
* Create DTO — server-derived and server-computed fields are ABSENT BY CONSTRUCTION, so `.strict()`
* refuses a client that tries to set a ceiling, the usage count, or its own default flag.
*/
const createMachineTypeSlugSchema = machineTypeSlugCatalogEntrySchema.omit({
	maxVCPUMilli: true,
	maxMemMiB: true,
	usageCount: true,
	isDefault: true
}).strict();
/**
* @deprecated compute-classes D5 — superseded by compute CLASSES + the D1 resolver
* (`compute-class.ts`). Never built; zero production consumers. Retained for out-of-tree
* consumers only; deleted in a deliberate major.
*
* Update DTO. ⛔ `slug` is ABSENT BY CONSTRUCTION (M-10 immutability), so `.strict()` refuses it —
* the same discipline `updateDcrRedirectHostSchema` uses for `host`, and for the same reason: editing
* an identifier in place turns every existing reference into a lie.
*/
const updateMachineTypeSlugSchema = createMachineTypeSlugSchema.omit({ slug: true }).partial().strict();
//#endregion
export { MACHINE_TYPE_CLOUDS, ONLINE_MACHINE_TYPE_CLOUDS, createMachineTypeSlugSchema, machineTypeCloudMappingSchema, machineTypeSlugCatalogEntrySchema, updateMachineTypeSlugSchema };

//# sourceMappingURL=machine-type-slug-catalog.js.map